import { type ModuleKey } from './constants';
import type { ModuleDefinition, FieldValue } from './module-kit';
import { fieldOf, readField, validateField, writeField } from './module-kit';
import { format, isMessageKey, m, verbatim, type Message, type PlainKey } from '@/i18n';

/**
 * The module registry: one `ModuleDefinition` (the standardized interface in
 * `module-kit.ts`) per `ModuleKey`. The core hosts iterate this; adding a module is an
 * entry here + a key in `MODULE_KEYS`, never a new table or write path. The helpers at
 * the bottom are the stable, key-addressed public API the server and reads call.
 */
/** Allocation can be stated in whichever unit a group thinks in; the renderer reads
 *  the unit alongside the amount. FTE is dimensionless (a share of one person). */
export const ALLOCATION_UNITS = ['fte', 'percent', 'days', 'hours'] as const;
/** One-time vs a recurring rate, so "0.5 FTE every week" and "3 days once" coexist. */
export const EFFORT_CADENCES = ['one_time', 'per_week', 'per_month'] as const;
export const ESTIMATE_UNITS = ['days', 'hours'] as const;
export const CURRENCIES = ['EUR', 'USD', 'GBP'] as const;
export const BUDGET_CADENCES = ['one_time', 'per_month', 'per_year'] as const;
/** The grain of a relative offset ("2 weeks after item X") and of a duration. */
export const OFFSET_UNITS = ['days', 'weeks', 'months'] as const;

/** A module's word in English, from the catalog (the one source; the surfaces
 *  format the same key in the reader's locale through `moduleWords`). */
const en = (key: PlainKey): string => format('en', m(key));

export const MODULES: Record<ModuleKey, ModuleDefinition> = {
  // Resources: planned capacity on this item, stated in whichever unit a group thinks
  // in (the amount carries its unit, so it reads honestly without forcing one scale).
  // Three independent lines: effort (allocation), a planning estimate, and budget; each
  // a small set of scalar fields the custom renderer composes (DESIGN.md Prinzip 10).
  resources: {
    key: 'resources',
    label: en('modules.resources.label'),
    description: en('modules.resources.description'),
    fields: [
      {
        key: 'allocation',
        label: en('modules.resources.allocation.label'),
        type: 'number',
        format: 'fte-fraction',
        source: 'authored',
        owner: 'contributor',
        aggregate: 'sum',
        visibility: 'public',
        min: 0,
        help: en('modules.resources.allocation.help'),
      },
      {
        key: 'allocationUnit',
        label: en('modules.resources.allocationUnit.label'),
        type: 'enum',
        options: ALLOCATION_UNITS,
        source: 'authored',
        owner: 'contributor',
        aggregate: 'none',
        visibility: 'public',
      },
      {
        key: 'allocationCadence',
        label: en('modules.resources.allocationCadence.label'),
        type: 'enum',
        options: EFFORT_CADENCES,
        source: 'authored',
        owner: 'contributor',
        aggregate: 'none',
        visibility: 'public',
        help: en('modules.resources.allocationCadence.help'),
      },
      {
        key: 'estimate',
        label: en('modules.resources.estimate.label'),
        type: 'number',
        source: 'authored',
        owner: 'contributor',
        aggregate: 'sum',
        visibility: 'public',
        min: 0,
        help: en('modules.resources.estimate.help'),
      },
      {
        key: 'estimateUnit',
        label: en('modules.resources.estimateUnit.label'),
        type: 'enum',
        options: ESTIMATE_UNITS,
        source: 'authored',
        owner: 'contributor',
        aggregate: 'none',
        visibility: 'public',
      },
      {
        key: 'budget',
        label: en('modules.resources.budget.label'),
        type: 'number',
        format: 'currency',
        source: 'authored',
        owner: 'either',
        aggregate: 'sum',
        visibility: 'public',
        min: 0,
        help: en('modules.resources.budget.help'),
      },
      {
        key: 'budgetCurrency',
        label: en('modules.resources.budgetCurrency.label'),
        type: 'enum',
        options: CURRENCIES,
        source: 'authored',
        owner: 'either',
        aggregate: 'none',
        visibility: 'public',
      },
      {
        key: 'budgetCadence',
        label: en('modules.resources.budgetCadence.label'),
        type: 'enum',
        options: BUDGET_CADENCES,
        source: 'authored',
        owner: 'either',
        aggregate: 'none',
        visibility: 'public',
      },
    ],
  },

  // Schedule: a start / end / duration triad. Start and end are each either an absolute
  // TimeValue **or** an anchor (another item + an offset) - "two weeks after Ship API".
  // Anchors are recorded, not yet auto-resolved (the board item "Known gaps (July):
  // modules and unbuilt ideas"). The `late` fact the reconciliation engine reads off the
  // absolute `endTime`; it produces a neutral rank, never a health grade
  // (docs/RECONCILIATION.md).
  schedule: {
    key: 'schedule',
    label: en('modules.schedule.label'),
    description: en('modules.schedule.description'),
    fields: [
      // --- start: absolute time, or anchor + offset ---
      {
        key: 'startTime',
        label: en('modules.schedule.startTime.label'),
        type: 'timevalue',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        help: en('modules.schedule.startTime.help'),
      },
      {
        key: 'startAnchor',
        label: en('modules.schedule.startAnchor.label'),
        type: 'ref',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        help: en('modules.schedule.startAnchor.help'),
      },
      {
        key: 'startOffset',
        label: en('modules.schedule.startOffset.label'),
        type: 'number',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
      {
        key: 'startOffsetUnit',
        label: en('modules.schedule.startOffsetUnit.label'),
        type: 'enum',
        options: OFFSET_UNITS,
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
      // --- end: absolute time, or anchor + offset ---
      {
        key: 'endTime',
        label: en('modules.schedule.endTime.label'),
        type: 'timevalue',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        help: en('modules.schedule.endTime.help'),
      },
      {
        key: 'endAnchor',
        label: en('modules.schedule.endAnchor.label'),
        type: 'ref',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        help: en('modules.schedule.endAnchor.help'),
      },
      {
        key: 'endOffset',
        label: en('modules.schedule.endOffset.label'),
        type: 'number',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
      {
        key: 'endOffsetUnit',
        label: en('modules.schedule.endOffsetUnit.label'),
        type: 'enum',
        options: OFFSET_UNITS,
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
      // --- duration ---
      {
        key: 'duration',
        label: en('modules.schedule.duration.label'),
        type: 'number',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        min: 0,
        help: en('modules.schedule.duration.help'),
      },
      {
        key: 'durationUnit',
        label: en('modules.schedule.durationUnit.label'),
        type: 'enum',
        options: OFFSET_UNITS,
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
      // --- cadence: the declared BEAT (MACHINE.md §1 row 9, "dates OR cadence") ---
      // A CLAIMS-class declared temporal intention: "this item is expected to
      // move/beat at roughly this rhythm". Lives on the schedule module because
      // it is the same temporal-intentions axis as start/end (consolidate; no
      // new module). Stored as a beat count + unit, mirroring duration exactly
      // (number + OFFSET_UNITS). It INFORMS the decay/quiet machinery (a
      // weekly-cadence standing service is healthy while quiet for a few days),
      // never dictates: the reader is `domain/cadence.ts` (`readCadence`), which
      // the fact engine consults for a tempo-relative quiet threshold and which
      // falls back to the flat constants when undeclared.
      {
        key: 'cadence',
        label: en('modules.schedule.cadence.label'),
        type: 'number',
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
        min: 1,
        help: en('modules.schedule.cadence.help'),
      },
      {
        key: 'cadenceUnit',
        label: en('modules.schedule.cadenceUnit.label'),
        type: 'enum',
        options: OFFSET_UNITS,
        source: 'authored',
        owner: 'requester',
        aggregate: 'none',
        visibility: 'public',
      },
    ],
  },

  // Checklist: the lines of the item's list and the order of the whole list
  // (docs/CHECKLIST.md §2, ruled 2026-11-03). Its data is not a set of authored
  // fields a section edits: every change is a line act or a move, each its own
  // command with its own pure decider (`src/domain/checklist.ts`) and one event
  // naming the line by its field path (`src/server/checklist.ts`). So it
  // declares no field, and the generic field write refuses it. It is no
  // section either (`SECTION_MODULE_KEYS`): the list stands under the head of
  // every item from its birth.
  checklist: {
    key: 'checklist',
    label: en('modules.checklist.label'),
    description: en('modules.checklist.description'),
    fields: [],
  },
};

/**
 * A platform module's words as descriptors, for a surface that speaks the
 * reader's locale (docs/I18N.md §6): its name, its description, and each
 * field's label and help. `MODULES` keeps the English of the same keys, which
 * a refusal and the agent tools read.
 */
export const moduleWords = {
  label: (key: ModuleKey): Message => m(`modules.${key}.label`),
  description: (key: ModuleKey): Message => m(`modules.${key}.description`),
  fieldLabel: (key: ModuleKey, fieldKey: string): Message =>
    keyed(`modules.${key}.${fieldKey}.label`) ??
    verbatim(fieldOf(MODULES[key], fieldKey)?.label ?? fieldKey),
  fieldHelp: (key: ModuleKey, fieldKey: string): Message | undefined =>
    keyed(`modules.${key}.${fieldKey}.help`),
};

/** A plain catalog entry by a key built from data, or `undefined`. */
function keyed(key: string): Message | undefined {
  return isMessageKey(key) ? ({ key } as Message) : undefined;
}

export function moduleDef(key: ModuleKey): ModuleDefinition {
  return MODULES[key];
}

export function fieldDef(key: ModuleKey, fieldKey: string) {
  return fieldOf(MODULES[key], fieldKey);
}

/** A stored module field value (re-exported for callers that don't need the kit). */
export type ModuleFieldValue = FieldValue;

// --- key-addressed wrappers (the stable public API; the kit functions take a def) ---

export function validateModuleField(moduleKey: ModuleKey, fieldKey: string, value: unknown) {
  return validateField(MODULES[moduleKey], fieldKey, value);
}

export function readModuleField(
  moduleData: Record<string, unknown>,
  moduleKey: ModuleKey,
  fieldKey: string,
): FieldValue {
  return readField(moduleData, moduleKey, fieldKey);
}

export function writeModuleField(
  moduleData: Record<string, unknown>,
  moduleKey: ModuleKey,
  fieldKey: string,
  value: FieldValue,
): Record<string, unknown> {
  return writeField(moduleData, moduleKey, fieldKey, value);
}
